import _ from "lodash";
import { canUserPerformAction } from "access-roles/general-functions";
import { FormattedMessage } from "react-intl";

import {
  COEF_POSITION_FUNCTION,
  CONTRACTUAL_STATUS,
  DASHBOARD_REQUIRED_ROLES,
  DATEPICKER_DEFAULT_SETTINGS,
  DELIVERY_NOTES_SECTIONS,
  LANG,
  MANAGER_TOOLS_REQUIRED_ROLES,
  MANAGER_TOOLS_SECTIONS,
  MUTUAL_DASHBOARD_REQUIRED_ROLES,
  QUERY_STATUS,
  ROLE_MATCH_MODE,
  routeMappings,
  MANAGER_TOOLS_PATH,
} from "constants/constants";
import { ENTITY } from "constants/info-header";
import {
  MISSION_ROLES,
  ROLE_GET_CORRESPONDANCE,
  ROLE_GET_MISSION_BILL_TABLE,
  ROLE_IMPORT_EXTERNAL_TRAINING,
  ROLE_IMPORT_MISSION_BILL,
} from "access-roles/roles";
import LoaderV2 from "core/loader/LoaderV2";
import { RULES } from "constants/forms";

export const Scopes = ["perso", "macro", "admin", "read"];

export const pathSegmentsHelper = ({ pathname }) =>
  pathname.split("/").filter(Boolean);

export const dataLoading = (data, status) => {
  if (status === QUERY_STATUS.LOADING)
    return <LoaderV2 loaderSize={"small-loader"} />;
  else return data;
};

export const cleanUrlV2 = (params) => {
  const queryParts = [];

  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      value.forEach((val) => {
        return queryParts.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(val)}`
        );
      });
    } else if (
      value !== undefined &&
      value !== null &&
      value !== "" &&
      value.length !== 0
    ) {
      return queryParts.push(
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
      );
    }
  });

  return queryParts.join("&");
};

export const cleanUrl = (paramsObject) => {
  const params = new URLSearchParams();

  const transformArrayToCSV = (arr) =>
    Array.isArray(arr) ? arr.join(",") : arr;

  for (const [key, value] of Object.entries(paramsObject)) {
    if (
      value !== undefined &&
      value !== null &&
      !(typeof value === "string" && value.trim() === "") &&
      (!Array.isArray(value) || value.length > 0)
    ) {
      if (Array.isArray(value)) {
        if (key === "mission_type") {
          value.forEach((item) => params.append(key, item));
        } else {
          params.append(key, transformArrayToCSV(value));
        }
      } else {
        params.append(key, value);
      }
    }
  }

  const queryString = params.toString().replace(/%2C/g, ",");
  return queryString;
};

export const withUserScopeCheck = async ({ callback, scope }) => {
  if (!scope) {
    console.error("userScope is not defined");
    return;
  }
  try {
    return await callback();
  } catch (e) {
    // Don't log aborted requests - these are normal cancellations
    if (e?.message !== "Request aborted" && e?.code !== "ERR_CANCELED") {
      console.error("API call failed", e);
    }
    throw e;
  }
};

export const formatDate = (dateString, lang) => {
  const formatLang = lang === LANG.FR ? "fr-FR" : "en-US";
  const options = { day: "2-digit", month: "2-digit", year: "numeric" };
  return new Date(dateString).toLocaleDateString(formatLang, options);
};

export const downloadFile = async ({
  file_id,
  fileType,
  fileName,
  date,
  downloadFileQuery,
  userScope,
  lang,
  status,
  queryParams,
  documentTag,
  BASE_URL,
  section,
}) => {
  try {
    const file = await downloadFileQuery({
      file_id,
      userScope,
      date,
      lang,
      status,
      queryParams,
      documentTag,
      BASE_URL,
      section,
    });
    const blob = new Blob([file], { type: fileType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error("Erreur lors du téléchargement du fichier:", error);
  }
};

const toolsManagerRoutesSettings = () =>
  Object.values(MANAGER_TOOLS_SECTIONS).map((e) => ({
    path: Scopes.map((scope) => `/${scope}/${MANAGER_TOOLS_PATH}/${e}`),
    translationId: "manager_tools_page_title",
    defaultMessage: "Outils manager",
  }));

const deliveryNotesRoutesSettings = () =>
  Object.values(DELIVERY_NOTES_SECTIONS).map((e) => ({
    path: Scopes.map((scope) => `/${scope}/delivery-notes/${e}`),
    translationId: "delivery_notes_page_title",
    defaultMessage: "Bons de livraison",
  }));

export const ContentNavigationDetail = [
  ...toolsManagerRoutesSettings(),
  ...deliveryNotesRoutesSettings(),
  {
    path: Scopes.map((e) => `/${e}/dashboard`),
    translationId: "dashboard_page_title",
    defaultMessage: "Tableau de bord",
  },
  {
    path: Scopes.map((e) => `/${e}/dashboard/shared/ic`),
    translationId: "shared_dashboard_page_title",
    defaultMessage: "Tableau de bord mutualisé",
  },
  {
    path: Scopes.map((e) => `/${e}/dashboard/shared/fdm`),
    translationId: "shared_dashboard_page_title",
    defaultMessage: "Tableau de bord mutualisé",
  },
  {
    path: Scopes.map((e) => `/${e}/dashboard/shared/ed`),
    translationId: "shared_dashboard_page_title",
    defaultMessage: "Tableau de bord mutualisé",
  },
  {
    path: Scopes.map((e) => `/${e}/dashboard/shared/ok`),
    translationId: "shared_dashboard_page_title",
    defaultMessage: "Tableau de bord mutualisé",
  },
  {
    path: Scopes.map((e) => `/${e}/dashboard/shared/cl`),
    translationId: "shared_dashboard_page_title",
    defaultMessage: "Tableau de bord mutualisé",
  },
  {
    path: Scopes.map((e) => `/${e}/dashboard/shared/ko`),
    translationId: "shared_dashboard_page_title",
    defaultMessage: "Tableau de bord mutualisé",
  },
  {
    path: Scopes.map((e) => `/${e}/dashboard/correspondence`),
    translationId: "correspondence_dashboard_page_title",
    defaultMessage: "Tableau de correspondance",
  },
  {
    path: Scopes.map((e) => `/${e}/directory/staff`),
    translationId: "directory_staff_page_title",
    defaultMessage: "Annuaire staff",
  },
  {
    path: Scopes.map((e) => `/${e}/directory/str`),
    translationId: "directory_str_page_title",
    defaultMessage: "Annuaire sous-traitants",
  },
  {
    path: Scopes.map((e) => `/${e}/directory/customers`),
    translationId: "directory_customers_page_title",
    defaultMessage: "Annuaire clients",
  },
  {
    path: Scopes.map((e) => `/${e}/directory/company/billing`),
    translationId: "directory_billing_client_page_title",
    defaultMessage: "Annuaire société de facturation",
  },
  {
    path: Scopes.map((e) => `/${e}/directory/company/str`),
    translationId: "directory_str_company_page_title",
    defaultMessage: "Annuaire société sous-traitante",
  },
  {
    path: Scopes.map((e) => `/${e}/inputs/import-billing`),
    translationId: "inputs_table_billing_page_title",
    defaultMessage: "Import facturation",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/reasonleave`),
    params: [":id"],
    translationId: "admin_reasonleave_page",
    defaultMessage: "Page admin raison départ",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/reasonleave/edit`),
    params: [":id"],
    translationId: "admin_reasonleave_page_edit",
    defaultMessage: "Page admin raison départ - Edition",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/reasonleave/new`),
    translationId: "admin_reasonleave_page_new",
    defaultMessage: "Page admin raison départ - Création",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/branches`),
    params: [":id"],
    translationId: "admin_branches_page",
    defaultMessage: "Page admin filiales",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/country-holidays`),
    translationId: "admin_holidays_page",
    defaultMessage: "Vacances par pays",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/regions`),
    params: [":id"],
    translationId: "admin_regions_page",
    defaultMessage: "Page admin régions",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/countries`),
    params: [":id"],
    translationId: "admin_countries_page",
    defaultMessage: "Page admin pays",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/countries/new`),
    params: [":id"],
    translationId: "admin_countries_page",
    defaultMessage: "Page admin pays",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/countries/edit`),
    params: [":id"],
    translationId: "admin_countries_page",
    defaultMessage: "Page admin pays",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/currency`),
    params: [":id"],
    translationId: "admin_currency_page",
    defaultMessage: "Page admin devise",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/exchange-rate`),
    params: [":id"],
    translationId: "admin_exchange_rate_page",
    defaultMessage: "Page admin taux de change",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/holidays`),
    params: [":id"],
    translationId: "admin_holidays_page",
    defaultMessage: "Vacances par pays",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/exchange_rate/new`),
    params: [":id"],
    translationId: "admin_exchange_rate_page",
    defaultMessage: "Page admin taux de change",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/exchange_rate/edit`),
    params: [":id"],
    translationId: "admin_exchange_rate_page",
    defaultMessage: "Page admin taux de change",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/currency/new`),
    params: [":id"],
    translationId: "admin_currency_page",
    defaultMessage: "Page admin devise - Création",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/currency/edit`),
    params: [":id"],
    translationId: "admin_currency_page",
    defaultMessage: "Page admin devise - Edition",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/coefficient`),
    params: [":id"],
    translationId: "admin_coefficient_page",
    defaultMessage: "Page admin coefficient",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/coefficient/new`),
    params: [":id"],
    translationId: "admin_coefficient_page_new",
    defaultMessage: "Page admin coefficient - Création",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/coefficient/edit`),
    params: [":id"],
    translationId: "admin_coefficient_page_edit",
    defaultMessage: "Page admin coefficient - Edition",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/position`),
    params: [":id"],
    translationId: "admin_position_page",
    defaultMessage: "Page admin position",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/position/edit`),
    params: [":id"],
    translationId: "admin_position_page_edit",
    defaultMessage: "Page admin position - Edition",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/position/new`),
    params: [":id"],
    translationId: "admin_position_page_new",
    defaultMessage: "Page admin position - Création",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/typecompanies`),
    params: [":id"],
    translationId: "admin_typecompanies_page",
    defaultMessage: "Page admin type de sociétés",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/recruitmentmethods`),
    params: [":id"],
    translationId: "admin_recruitmentmethods_page",
    defaultMessage: "Page admin mode de recrutement",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/sector`),
    params: [":id"],
    translationId: "admin_sector_page",
    defaultMessage: "Page admin secteur",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/syntec`),
    params: [":id"],
    translationId: "admin_syntec_page",
    defaultMessage: "Page admin syntec",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/syntec/new`),
    params: [":id"],
    translationId: "admin_syntec_page_new",
    defaultMessage: "Page admin syntec",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/syntec/edit`),
    params: [":id"],
    translationId: "admin_syntec_page_edit",
    defaultMessage: "Page admin syntec",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/syntecposcoef`),
    params: [":id"],
    translationId: "admin_syntecposcoef_page",
    defaultMessage: "Page admin coefficient & position",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/syntecposcoef/new`),
    params: [":id"],
    translationId: "admin_syntecposcoef_page",
    defaultMessage: "Page admin coefficient & position",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/syntecposcoef/edit`),
    params: [":id"],
    translationId: "admin_syntecposcoef_page",
    defaultMessage: "Page admin coefficient & position",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/syntecposcoef-staff`),
    params: [":id"],
    translationId: "admin_syntecposcoef_staff_executive_page",
    defaultMessage: "Page admin coefficient & position staff cadre",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/syntecposcoef-staff/new`),
    params: [":id"],
    translationId: "admin_syntecposcoef_staff_executive_page",
    defaultMessage: "Page admin coefficient & position staff cadre",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/syntecposcoef-staff/edit`),
    params: [":id"],
    translationId: "admin_syntecposcoef_staff_executive_page",
    defaultMessage: "Page admin coefficient & position staff cadre",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/jobs`),
    params: [":id"],
    translationId: "admin_jobs_page",
    defaultMessage: "Page admin métiers",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/jobs/edit`),
    params: [":id"],
    translationId: "admin_jobs_page_edit",
    defaultMessage: "Page admin métiers - Edition",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/jobs/new`),
    params: [":id"],
    translationId: "admin_jobs_page_new",
    defaultMessage: "Page admin métiers - Création",
  },
  {
    path: Scopes.map((e) => `/${e}/settings/recoursestr`),
    params: [":id"],
    translationId: "admin_recoursestr_page",
    defaultMessage: "Page admin recours str",
  },
  {
    path: Scopes.map((e) => `/${e}/missions`),
    params: [":id"],
    translationId: "missions_page_title",
    defaultMessage: "Missions",
  },
  {
    path: Scopes.map((e) => `/${e}/training`),
    translationId: "training_page_title",
    defaultMessage: "Formation",
  },
  {
    path: Scopes.map((e) => `/${e}/notifications`),
    translationId: "notifications_page_title",
    defaultMessage: "Mes notifications",
  },
  {
    path: Scopes.map((e) => `/${e}/inputs/spreadsheet`),
    translationId: "manager_tools_page_inputs_table",
    defaultMessage: "Tableau de saisie",
  },
  {
    path: Scopes.map((e) => `/${e}/staff/creation`),
    translationId: "add_staff_member",
    defaultMessage: "Ajouter un nouveau membre du staff",
  },
  {
    path: Scopes.map((e) => `/${e}/subcontractor-company/creation`),
    translationId: "add_subcontracting_company",
    defaultMessage: "Ajouter une société sous-traitante",
  },
  {
    path: Scopes.map((e) => `/${e}/billing-client/creation`),
    translationId: "add_billing_client",
    defaultMessage: "Ajouter une société de facturation",
  },
  {
    path: Scopes.map((e) => `/${e}/staff`),
    params: [":id"],
  },
  {
    path: Scopes.map((e) => `/${e}/subcontractor-company`),
    params: [":id"],
  },
  {
    path: Scopes.map((e) => `/${e}/consultant/creation`),
    translationId: "add_consultant_member",
    defaultMessage: "Ajouter un nouveau consultant",
  },
  {
    path: Scopes.map((e) => `/${e}/consultant`),
    params: [":id"],
  },
  {
    path: Scopes.map((e) => `/${e}/subcontractor/creation`),
    translationId: "add_subcontractor_member",
    defaultMessage: "Ajouter un nouveau sous-traitant",
  },
  {
    path: Scopes.map((e) => `/${e}/subcontractor`),
    params: [":id"],
  },
  {
    path: Scopes.map((e) => `/${e}/billing-client/creation`),
    translationId: "add_billing_client_member",
    defaultMessage: "Ajouter une société de facturation",
  },
  {
    path: Scopes.map((e) => `/${e}/billing-client`),
    params: [":id"],
  },
  {
    path: Scopes.map((e) => `/${e}/client/creation`),
    translationId: "add_client",
    defaultMessage: "Ajouter un nouveau client",
  },
  {
    path: Scopes.map((e) => `/${e}/client`),
    params: [":id"],
  },
  {
    path: Scopes.map((e) => `/${e}/missions/creation`),
    translationId: "add_mission",
    defaultMessage: "Ajouter une nouvelle mission",
  },
  {
    path: Scopes.map(() => `/authorize`),
    translationId: "authorize",
    defaultMessage: "authorize",
  },
  {
    path: Scopes.map((e) => `/${e}/authorize`),
    translationId: "authorize",
    defaultMessage: "authorize",
  },
];

const getFilteredDashboardItems = (rights) => {
  const dashboardItems = [
    {
      translationId: "header_menu_dashboard_shared",
      defaultMessage: "Tableau de bord mutualisé",
      path: Scopes.map((e) => `/${e}/dashboard/shared`),
      dropdownItemLink: "dashboard/shared/ic",
      requiredRoles: MUTUAL_DASHBOARD_REQUIRED_ROLES,
    },
    {
      translationId: "header_menu_dashboard",
      defaultMessage: "Tableau de bord",
      path: Scopes.map((e) => `/${e}/dashboard`),
      dropdownItemLink: "dashboard",
      requiredRoles: DASHBOARD_REQUIRED_ROLES,
    },
    {
      translationId: "header_menu_correspondence_table",
      defaultMessage: "Table de correspondance",
      path: Scopes.map((e) => `/${e}/dashboard/correspondence`),
      dropdownItemLink: "dashboard/correspondence",
      requiredRoles: ROLE_GET_CORRESPONDANCE,
    },
  ];

  const filteredItems = dashboardItems.filter((dropdownItem) =>
    canUserPerformAction({
      userRoles: rights,
      requiredRoles: dropdownItem.requiredRoles,
    })
  );

  return filteredItems;
};

export const contentHeader = ({ rights }) =>
  _.reject(
    [
      (() => {
        const filteredDropdownItems = getFilteredDashboardItems(rights);

        if (filteredDropdownItems.length > 0) {
          return {
            id: "dashboard",
            translationId: "header_menu_home_page",
            defaultMessage: "Dashboard",
            has_dropdown: true,
            active_dropdown: false,
            dropdownLink: "dashboard",
            dropdown_menu: filteredDropdownItems,
          };
        }

        return null;
      })(),
      canUserPerformAction({
        userRoles: rights,
        requiredRoles: MISSION_ROLES.ROLE_GET_MISSION_LIST,
      })
        ? {
            id: "missions",
            dropdownLink: "missions",
            translationId: "header_menu_missions_page",
            defaultMessage: "Missions",
            has_dropdown: false,
            path: Scopes.map((e) => `/${e}/missions`),
            active_dropdown: false,
          }
        : {},
      (() => {
        const hasInputTableAccess = canUserPerformAction({
          userRoles: rights,
          requiredRoles: ROLE_GET_MISSION_BILL_TABLE,
        });
        if (!hasInputTableAccess) return null;
        const inputTableItems = [
          {
            translationId: "header_menu_inputs_table_page",
            defaultMessage: "Tableau de saisie",
            path: Scopes.map((e) => `/${e}/inputs/spreadsheet`),
            dropdownItemLink: "inputs/spreadsheet",
            requiredRoles: ROLE_GET_MISSION_BILL_TABLE,
          },
          {
            translationId: "header_menu_inputs_table_billing",
            defaultMessage: "Import facturation",
            path: Scopes.map((e) => `/${e}/inputs/import-billing`),
            dropdownItemLink: "inputs/import-billing",
            requiredRoles: ROLE_IMPORT_MISSION_BILL,
          },
        ];
        const filteredDropdownItems = inputTableItems.filter((dropdownItem) =>
          canUserPerformAction({
            userRoles: rights,
            requiredRoles: dropdownItem.requiredRoles,
          })
        );
        return {
          id: "spreadsheet",
          path: Scopes.map((e) => `/${e}/inputs/spreadsheet`),
          translationId: "header_menu_inputs_table_page",
          defaultMessage: "Tableau de saisie",
          has_dropdown: true,
          active_dropdown: false,
          dropdownLink: "inputs/spreadsheet",
          dropdown_menu: filteredDropdownItems,
        };
      })(),

      canUserPerformAction({
        userRoles: rights,
        requiredRoles: ROLE_IMPORT_EXTERNAL_TRAINING,
      })
        ? {
            id: "training",
            dropdownLink: "training",
            translationId: "header_menu_training_page",
            defaultMessage: "Formation",
            has_dropdown: false,
            path: Scopes.map((e) => `/${e}/training`),
            active_dropdown: false,
          }
        : {},

      canUserPerformAction({
        userRoles: rights,
        requiredRoles: MANAGER_TOOLS_REQUIRED_ROLES,
        mode: ROLE_MATCH_MODE.OR,
      })
        ? {
            id: "manager",
            dropdownLink: MANAGER_TOOLS_PATH,
            translationId: "header_menu_manager_tools_page",
            defaultMessage: "Outils manager",
            has_dropdown: true,
            path: Scopes.map((e) => `/${e}/${MANAGER_TOOLS_PATH}`),
            active_dropdown: false,
            // The entry used to have no dropdown: clicking it went straight to
            // the tools. That click is preserved by HoverMenuButton (the parent
            // link is forced on menuUrls.manager), the menu only adds a second
            // destination. Both items are governed by the gate above, delivery
            // notes introducing no right of their own — hence no per-item
            // requiredRoles, unlike the spreadsheet menu whose items differ.
            dropdown_menu: [
              {
                translationId: "header_menu_manager_tools_page",
                defaultMessage: "Outils manager",
                path: Scopes.map((e) => `/${e}/${MANAGER_TOOLS_PATH}`),
                dropdownItemLink: MANAGER_TOOLS_PATH,
              },
              {
                translationId: "header_menu_delivery_notes_page",
                defaultMessage: "Bons de livraison",
                path: Scopes.map(
                  (e) =>
                    `/${e}/delivery-notes/${DELIVERY_NOTES_SECTIONS.saved}`
                ),
                dropdownItemLink: `delivery-notes/${DELIVERY_NOTES_SECTIONS.saved}`,
              },
            ],
          }
        : {},
    ],
    _.isEmpty
  );

export const renderIf = (condition, content1, content2) => {
  return condition ? content1 : content2;
};

export const translateHelper = ({ lang, fr, en }) => {
  return lang === LANG.FR ? fr : en;
};

export const getTranslatedList = ({ lang, list }) =>
  list?.map((elm) => ({
    label: translateHelper({ lang, fr: elm.fr, en: elm.en }),
    value: elm?.id,
  }));

const capitalizeLabel = (label) =>
  label ? label.charAt(0).toUpperCase() + label.slice(1) : label;

// Protected category (AUTRES) is the back's fallback, not an entry choice.
export const getJobCategoryOptions = ({ lang, list, type }) =>
  getTranslatedList({
    lang,
    list: (list || []).filter((elm) => !elm?.is_protected),
  }).map((option) => ({
    ...option,
    label: capitalizeLabel(option.label),
    ...(type && { type }),
  }));

export const handleSyntecCoefficientId = ({
  data,
  contractualStatusList,
  flattedsyntecCoefList,
  entity,
}) => {
  const { contractual_status_id, syntec_coef_position_staff } = data || {};

  const getContractualStatusId = (contractualStatus) =>
    contractualStatusList.find((elm) => elm.en === contractualStatus)?.id;

  if (
    contractual_status_id ===
      getContractualStatusId(CONTRACTUAL_STATUS.NO_EXECUTIVE) &&
    entity === ENTITY.CONSULTANT
  ) {
    return flattedsyntecCoefList?.find(
      (elm) => elm.value === COEF_POSITION_FUNCTION.LEARN
    ).id;
  } else if (
    contractual_status_id ===
    getContractualStatusId(CONTRACTUAL_STATUS.INTERNSHIP)
  ) {
    return flattedsyntecCoefList?.find(
      (elm) => elm.value === COEF_POSITION_FUNCTION.STAGE
    ).id;
  } else {
    return syntec_coef_position_staff[0]?.value;
  }
};

export const displayFileBrowser = ({ inputElem }) => {
  const inputFile = document.getElementById(inputElem);
  inputFile.click();
};

export const getEntityName = (id, namesByFilters) => {
  const entity = namesByFilters?.find((item) => item.id === id);
  return entity?.full_name;
};

export const getEntitySlug = (id, namesByFilters) => {
  const entity = namesByFilters?.find((item) => item.id === id);
  return entity?.slug;
};

export const getEntityIsAllowed = (id, namesByFilters) =>
  namesByFilters?.find((item) => item.id === id)?.is_allowed;

export const getEntityIsAllowedBySlug = (slug, namesByFilters) =>
  namesByFilters?.find((item) => item.slug === slug)?.is_allowed;

const ENTITY_ROUTE_SEGMENT_BY_TYPE = {
  cslt_str: ({ profile }) =>
    profile === "str" ? "subcontractor" : "consultant",
  cslt: () => "consultant",
  str: () => "subcontractor",
  people: () => "staff",
  clients: () => "client",
  prospections: () => "client",
  billing_clients: () => "billing-client",
};

export const buildEntityTagLink = ({ option, userScope, namesByFilters }) => {
  if (!option?.value || !userScope) return undefined;
  if (getEntityIsAllowed(option.value, namesByFilters) === false)
    return undefined;
  const resolver = ENTITY_ROUTE_SEGMENT_BY_TYPE[option?.type];
  if (!resolver) return undefined;
  const slug = getEntitySlug(option.value, namesByFilters);
  if (!slug) return undefined;
  const segment = resolver(option);
  if (!segment) return undefined;
  return `/${userScope}/${segment}/${slug}`;
};

export const DYB_PROFILE_BASE_URL = "https://showcase.doyoubuzz.com/a/user";

export const buildDybProfileUrl = (dybShowcaseId) =>
  `${DYB_PROFILE_BASE_URL}/${dybShowcaseId}/profile`;

export const uploadFileHelper = ({
  file,
  type,
  userId,
  uploadQueryFunc,
  uploadProfilePictureFunc,
}) => {
  if (type === "profile-picture" && uploadProfilePictureFunc && userId) {
    uploadProfilePictureFunc({ userId, file });
  } else {
    const params = ({ id, data }) =>
      ["profile-picture", "signature-picture"].includes(type)
        ? data
        : { id, data };

    // No upload function means the caller only wants the picked file back
    // through onChange, without sending it anywhere.
    if (file && uploadQueryFunc) {
      const reader = new FileReader();
      reader.onload = () => {
        const binaryData = reader.result;
        uploadQueryFunc(params({ id: userId, data: binaryData }));
      };
      reader.onerror = () => {
        console.error("Error reading file");
      };
      reader.readAsArrayBuffer(file);
    }
  }
};

export const getDefaultRoutes = (userRoles) => {
  const pageRoute = routeMappings.find(({ role }) =>
    Array.isArray(role) && role.length
      ? role.some((r) => userRoles.includes(r))
      : userRoles.includes(role)
  );
  return pageRoute?.route || "";
};

export const parseCheckboxValue = (value) => {
  const toParse = value || "[]";
  const parsed = JSON.parse(toParse);
  if (Array.isArray(parsed)) {
    return parsed.map((elem) => String(elem));
  }
  return [];
};

export const stringLengthWithoutSpaces = ({ string }) =>
  string?.replace(/\s+/g, "")?.length;

export const handleApiErrorResponse = ({
  apiErrorResponseList,
  errorSearched,
}) => !!apiErrorResponseList?.find((e) => e?.code === errorSearched);

export const formatNationalityLabel = (nationality, lang) => ({
  label:
    lang === "FR" || lang === "fr"
      ? nationality.nationality_fr
      : nationality.nationality_eng,
  value: nationality.id,
});

export const formatNationalityValue = (nationality, nationalities, lang) => {
  if (!nationality || !nationalities?.length) return null;

  const findNationality = (id) => nationalities.find((n) => n?.id === id);

  if (typeof nationality === "object" && !Array.isArray(nationality)) {
    const nat = findNationality(nationality?.id);
    return nat ? formatNationalityLabel(nat, lang) : null;
  }

  if (Array.isArray(nationality) && nationality.length > 0) {
    const nat = findNationality(nationality[0]?.value);
    return nat ? formatNationalityLabel(nat, lang) : null;
  }

  return null;
};

export const extractValue = (field, transform = (val) => val) => {
  if (Array.isArray(field) && field.length > 0) {
    if (field[0]?.value !== undefined) {
      return transform(field[0].value);
    }
    return transform(field[0]);
  }
  return field !== undefined ? transform(field) : null;
};

export const safeValue = (value, fallback = null) =>
  value === "" || value === null || value === undefined ? fallback : value;

export function getErrorCellClass(field, errors, watch, cellHighlight) {
  if (field.hasError === false) return "";
  const isEmpty =
    !watch(field.id) ||
    (Array.isArray(watch(field.id)) && watch(field.id)?.length === 0);
  if (
    (isEmpty && cellHighlight) ||
    errors?.[field.id] ||
    (field.id === "nationality" && errors?.nationality)
  ) {
    return "errorCellColor";
  }
  return "";
}

export const formatInputDateValue = (inputStr) => {
  const numbersOnly = inputStr.replace(/\D/g, "");
  const truncated = numbersOnly.slice(0, 8);

  if (truncated.length >= 5) {
    return `${truncated.slice(0, 2)}/${truncated.slice(2, 4)}/${truncated.slice(
      4
    )}`;
  } else if (truncated.length >= 3) {
    return `${truncated.slice(0, 2)}/${truncated.slice(2)}`;
  } else {
    return truncated;
  }
};

const parseDateParts = (parts, lang) => {
  const [part1, part2, part3] = parts;
  const parsed = [
    parseInt(part1, 10),
    parseInt(part2, 10),
    parseInt(part3, 10),
  ];

  return lang === LANG.EN
    ? { month: parsed[0], day: parsed[1], year: parsed[2] }
    : { day: parsed[0], month: parsed[1], year: parsed[2] };
};

const normalizeYear = (year) => {
  if (year.toString().length > 4) return null;

  if (year < 100) {
    const currentYear = new Date().getFullYear();
    const currentCentury = Math.floor(currentYear / 100) * 100;
    year = currentCentury + year;
    if (year > currentYear + 20) {
      year -= 100;
    }
  }

  return year;
};

const validateDateComponents = (day, month, year) => {
  return (
    year >= DATEPICKER_DEFAULT_SETTINGS.MIN_YEAR &&
    year <= DATEPICKER_DEFAULT_SETTINGS.MAX_YEAR &&
    month >= DATEPICKER_DEFAULT_SETTINGS.MIN_MONTH &&
    month <= DATEPICKER_DEFAULT_SETTINGS.MAX_MONTH &&
    day >= DATEPICKER_DEFAULT_SETTINGS.MIN_DAY &&
    day <= DATEPICKER_DEFAULT_SETTINGS.MAX_DAY
  );
};

const isValidDate = (day, month, year) => {
  return new Date(year, month - 1, day).getDate() === day;
};

export const parseInputDate = (inputStr, lang) => {
  const parts = inputStr.split("/");
  if (parts.length !== 3 || parts.some((part) => part === "")) return null;

  const { day, month, year } = parseDateParts(parts, lang);

  const normalizedYear = normalizeYear(year);
  if (normalizedYear === null) return null;

  if (!validateDateComponents(day, month, normalizedYear)) return null;
  if (!isValidDate(day, month, normalizedYear)) return null;

  return new Date(normalizedYear, month - 1, day);
};

// Helper function to format date for API without timezone issues
export const formatDateForAPI = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const calculateDatePickerPosition = (containerRef) => {
  if (containerRef.current) {
    const rect = containerRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;

    // Default position below the input
    let top = rect.bottom + 5;
    let left = rect.left;

    // If not enough space below, display above
    if (top + 300 > windowHeight) {
      top = rect.top - 305;
    }

    // If not enough space on the right, adjust
    if (left + 280 > windowWidth) {
      left = windowWidth - 285;
    }

    return { top, left };
  }
  return { top: 0, left: 0 };
};

export const hasDatePickerValueChanged = (
  inputType,
  prevValuesRef,
  inputConfigs
) => {
  const prev = prevValuesRef.current[inputType];
  const current = inputConfigs[inputType];

  return (
    prev.value !== current.currentValue ||
    prev.inputValue !== current.currentInputValue
  );
};

export const updateDatePickerInputValues = (
  inputType,
  inputConfigs,
  updateDateValue,
  prevValuesRef
) => {
  const config = inputConfigs[inputType];

  updateDateValue(
    config.currentValue,
    config.currentInputValue,
    config.setters.setSelectedDate,
    config.setters.setInputValue
  );

  prevValuesRef.current[inputType] = {
    value: config.currentValue,
    inputValue: config.currentInputValue,
  };
};

export const formattedCountry = ({ countries, value, lang }) => {
  const country = countries?.find((e) => value === e?.id);
  if (country) {
    return (lang === LANG.FR ? country?.name_fr : country?.name) || value;
  } else return value;
};

export const boolToStringHelper = ({ isTrue }) => (
  <FormattedMessage
    id={isTrue ? "yes" : "no"}
    defaultMessage={isTrue ? "Oui" : "Non"}
  />
);

export const parseValue = (val) => {
  if (!val) return null;
  if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
    const [y, m, d] = val.split("-").map(Number);
    return new Date(y, m - 1, d);
  }
  const parsed = new Date(val);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const isCreateToDisplayNavigation = (fromPath, toPath) => {
  // Regex to match creation paths like "/staff/create", "/client/create", etc.
  const createPathRegex = RULES.create_path_pattern;

  // Regex to match display paths like "/staff/123", "/client/456/edit", etc.
  const displayPathRegex = RULES.display_path_pattern;

  // Extract entity names from both paths
  const fromMatch = fromPath.match(createPathRegex);
  const toMatch = toPath.match(displayPathRegex);

  // Return true if both paths match their respective patterns
  // and the entity names are the same (e.g., "staff" in both paths)
  return fromMatch && toMatch && fromMatch[1] === toMatch[1];
};

export const getISODate = (monthsAgo = 0) => {
  const date = new Date();
  if (monthsAgo) date.setMonth(date.getMonth() - monthsAgo);
  return date.toISOString().split("T")[0];
};

export const statusHandler = ({ statusList }) => {
  if (statusList.some((status) => status === QUERY_STATUS.LOADING))
    return QUERY_STATUS.LOADING;
  if (statusList.some((status) => status === QUERY_STATUS.ERROR))
    return QUERY_STATUS.ERROR;
  if (statusList.some((status) => status === QUERY_STATUS.SUCCESS))
    return QUERY_STATUS.SUCCESS;
  return QUERY_STATUS.INITIAL;
};

export const syncRole = (toggleValue, roleName, role, setValue) => {
  if (!role) return;

  const hasRole = role.includes(roleName);

  if (toggleValue && !hasRole) {
    setValue("role", [...role, roleName]);
  } else if (!toggleValue && hasRole) {
    setValue(
      "role",
      role.filter((r) => r !== roleName)
    );
  }
};

export const setListUniqByIdHelper = (old, result) =>
  _.chain(old)
    .concat(result)
    .uniqBy((elm) => elm?.id)
    .value();

export const getCellErrorHelper = ({
  hasValue,
  hasError = false,
  cellHighlight,
}) => ((!hasValue && cellHighlight) || hasError ? "errorCellColor" : "");
